import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { programApi } from '../../shared/api/program.js';
import { mensajeDe } from '../../shared/errores.js';
import { useAuth } from '../../shared/auth/useAuth.js';
import { Boton } from '../../shared/ui/Boton.jsx';
import { Cargador } from '../../shared/ui/Cargador.jsx';
import { TemaToggle } from '../../shared/ui/Tema.jsx';

/**
 * Portada pública.
 *
 * ── Por qué la portada pide el programa y no lo inventa ─────────────────────
 *
 * La tentación es escribir siete meses a mano en el JSX, porque es rápido y
 * siempre sale bien. Sería una copia del contenido con dos problemas: la segunda
 * vez que se edite el temario habría que acordarse de editar también la portada,
 * y un visitante vería months que ya no existen. `/api/program` es público y
 * devuelve lo mismo para todo el mundo, así que la portada lee la misma fuente
 * que el temario y no puede mentir sobre el tamaño del programa.
 *
 * ── Por qué los números son del temario y no promesas ───────────────────────
 *
 * El número de ejercicios sale de contar los `exerciseCount` que el backend ya
 * calcula, no de un número escrito a mano. No se promete nada que no funcione
 * hoy: lo que hay es un temario escrito y unos tests que el navegador ejecuta de
 * verdad contra el código del alumno.
 */

/** Las tres cosas que el temario ofrece, con lo que ya hay dentro. */
const PILARES = [
  {
    titulo: 'Lecciones ordenadas',
    icono: '📚',
    texto:
      'Un temario de JavaScript full stack en orden: los fundamentos, el navegador, el backend, la base de datos y el despliegue. Cada lección dice qué vas a saber hacer al terminar.',
  },
  {
    titulo: 'Tests que se ejecutan',
    icono: '🧪',
    texto:
      'Los ejercicios llevan sus casos de prueba y se ejecutan en tu navegador contra tu código. Te dicen qué recibiste, qué esperaban y dónde se ha quedado colgado.',
  },
  {
    titulo: 'Proyectos y portfolio',
    icono: '🚀',
    texto:
      'Toca backend real, base de datos y despliegue. Los proyectos de cada mes salen de aquí, no de una carpeta aparte.',
  },
];

/** Colores por tipo de segmento para el código de ejemplo. */
const COLOR_LINEA = {
  c: 'text-codigo-300 italic',
  k: 'text-acento-300 font-medium',
  s: 'text-ok-300',
  f: 'text-brand-300',
  p: 'text-codigo-100',
};

/**
 * Ejemplos que se enseñan en la portada. Son datos, no HTML: cada línea es una
 * lista de `[tipo, texto]` que se pinta programáticamente, igual que los
 * bloques de las lecciones, para no meter contenido a pelo en el marcado.
 */
const EJEMPLOS = [
  {
    archivo: 'hola.js',
    titulo: 'Hola, JavaScript',
    lineas: [
      [['c', '// Esto corre en tu navegador, aquí mismo']],
      [['k', 'const'], ['p', ' saludo = '], ['s', '"Hola, mundo"'], ['p', ';']],
      [['f', 'console'], ['p', '.log(saludo);']],
      [['c', '// Cada lección termina con ejercicios y tests']],
    ],
  },
  {
    archivo: 'servidor.js',
    titulo: 'Backend y datos',
    lineas: [
      [['c', '// El temario llega hasta el despliegue']],
      [['k', 'const'], ['p', ' ruta = academia.iniciar();']],
      [['p', 'await ruta.aprender('], ['s', '"full stack"'], ['p', ');']],
      [['p', 'await ruta.frontend();']],
      [['p', 'await ruta.backend();']],
      [['f', 'ruta'], ['p', '.desplegar('], ['s', '"producción"'], ['p', ');']],
    ],
  },
  {
    archivo: 'pruebas.js',
    titulo: 'Tests de verdad',
    lineas: [
      [['c', '// Los tests corren contra tu código']],
      [['f', 'pruebas'], ['p', '.forEach('], ['k', 'async'], ['p', ' (t) => {']],
      [['p', '  const r = '], ['k', 'await'], ['p', ' ejecutar(t);']],
      [['f', 'console'], ['p', '.assert(r.ok, t.titulo);']],
      [['p', '});']],
    ],
  },
];

export function Home() {
  const { estado, usuario, loginAsGuest } = useAuth();
  const navegar = useNavigate();
  const [ejemplo, setEjemplo] = useState(0);

  const [programa, setPrograma] = useState(
    /** @type {import('@academy/shared').ProgramResponse | null} */ (null),
  );
  const [error, setError] = useState(/** @type {unknown} */ (null));

  const pedirPrograma = useCallback(() => {
    programApi
      .programa()
      .then((datos) => setPrograma(datos))
      .catch((fallo) => setError(fallo));
  }, []);

  useEffect(pedirPrograma, [pedirPrograma]);

  const destino = estado === 'autenticado' ? '/panel' : '/login';
  const texto = estado === 'autenticado' ? 'Ir a mi panel' : 'Empezar';

  const semanas = useMemo(
    () => programa?.months.reduce((total, mes) => total + mes.weeks.length, 0) ?? 0,
    [programa],
  );

  const ejercicios = useMemo(
    () => programa?.months.reduce((total, mes) => total + mes.exerciseCount, 0) ?? 0,
    [programa],
  );

  const mesesListos = useMemo(
    () => programa?.months.filter((mes) => mes.disponible).length ?? 0,
    [programa],
  );

  return (
    <div className="min-h-dvh">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-10 focus:rounded focus:bg-superficie focus:px-3 focus:py-2"
      >
        Saltar al contenido
      </a>

      <header className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-3 gap-y-3 px-4 py-4">
        <span className="inline-flex items-center gap-2.5 font-bold text-brand-600 dark:text-brand-800">
          <img
            src="/favicon.png"
            alt=""
            aria-hidden="true"
            className="size-9 rounded-xl ring-1 ring-ink-200"
          />
          JS Full Stack Academy
        </span>
        <div className="flex items-center gap-5">
          <Link
            to="/plan-de-estudios"
            className="text-sm font-medium text-ink-600 transition-colors hover:text-brand-700 dark:text-ink-400 dark:hover:text-brand-800"
          >
            Plan completo
          </Link>
          <Link
            to="/recursos"
            className="text-sm font-medium text-ink-600 transition-colors hover:text-brand-700 dark:text-ink-400 dark:hover:text-brand-800"
          >
            Recursos
          </Link>
          <TemaToggle />
          {estado === 'autenticado' ? (
            <span className="text-sm text-ink-500">Hola, {usuario?.name}</span>
          ) : (
            <Link to="/login">
              <Boton className="px-4 py-2">Iniciar sesión</Boton>
            </Link>
          )}
        </div>
      </header>

      <main id="contenido" className="mx-auto max-w-6xl px-4 pb-20">
        <section className="relative overflow-hidden rounded-3xl bg-noche text-white shadow-lg shadow-noche/40 ring-1 ring-white/10">
          {/*
            Fondo decorativo: dos focos de color muy difusos (índigo y violeta),
            una rejilla fina y una línea de luz arriba. Todo va `aria-hidden` y
            sin clics: es atmósfera, no contenido. La contención es deliberada,
            dos focos y una trama, para que se sienta futuro y no feria.
          */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <div className="absolute -top-28 -left-20 size-[30rem] rounded-full bg-brand-600/35 blur-[110px]" />
            <div className="absolute -right-16 -bottom-28 size-[28rem] rounded-full bg-acento-600/30 blur-[110px]" />
            <div className="fondo-rejilla absolute inset-0 opacity-60" />
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-brand-400/70 to-transparent" />
          </div>

          <div className="relative grid items-center gap-10 px-6 py-14 sm:px-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:py-20">
            <div className="min-w-0 text-center lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-white/90 backdrop-blur">
                <span className="relative flex size-1.5" aria-hidden="true">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-ok-400 opacity-75" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-ok-400" />
                </span>
                De cero a junior{programa ? ` en ${programa.meta.mesesTotales} meses` : ''}
              </span>

              <h1 className="mx-auto mt-6 max-w-xl text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:mx-0 lg:text-6xl">
                Aprende{' '}
                <span className="bg-linear-to-r from-brand-300 via-acento-300 to-brand-200 bg-clip-text text-transparent">
                  JavaScript full stack
                </span>
              </h1>

              <p className="mx-auto mt-6 max-w-xl text-base text-white/70 sm:text-lg lg:mx-0">
                {programa ? (
                  <>
                    {programa.meta.mesesTotales} meses, {semanas} semanas y {programa.meta.lessonCount}{' '}
                    lecciones escritas. Con {ejercicios} ejercicios que se ejecutan en tu navegador.
                  </>
                ) : (
                  'Lecciones, ejercicios y proyectos. Con backend propio, base de datos y despliegue.'
                )}
              </p>

              {/*
                El programa es público y no depende de la sesión. Si falla, la
                portada sigue siendo utilizable: los botones y el resto del texto no
                dependen de él, así que el error se enseña abajo y no se tapa la
                pantalla con un cartel.
              */}
              {error ? (
                <p className="mt-4 text-sm text-white/50">
                  No se ha podido cargar el programa ahora mismo ({mensajeDe(error)}).
                </p>
              ) : null}

              <div className="mt-9 flex flex-col items-center gap-4 lg:items-start">
                <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
                  {/*
                    Ver el programa va antes que crear cuenta: quien llega de un
                    buscador quiere saber qué se aprende antes de registrarse, y el
                    temario se lee entero sin cuenta.
                  */}
                  <Link
                    to={destino}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-linear-to-br from-coral-500 to-coral-600 px-6 py-3 text-base font-semibold text-coral-950 transition-all hover:from-coral-400 hover:to-coral-500 active:scale-[0.98]"
                  >
                    {texto}
                  </Link>
                  <Link
                    to="/programa"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-base font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
                  >
                    Ver el programa
                  </Link>
                </div>

                {estado === 'anonimo' ? (
                  <button
                    type="button"
                    onClick={() => {
                      loginAsGuest();
                      navegar('/panel');
                    }}
                    className="text-sm font-semibold text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
                  >
                    o pruébala como invitado →
                  </button>
                ) : null}
              </div>
            </div>

            {/*
              El escaparate: un panel de código de cristal con pestañas que
              cambian el ejemplo. Es lo primero que se ve a la derecha y comunica
              "esto es código real" sin una sola promesa de marketing.
            */}
            <div className="relative min-w-0">
              <div
                aria-hidden="true"
                className="absolute -inset-4 rounded-[2rem] bg-linear-to-tr from-brand-500/20 to-acento-500/20 blur-2xl"
              />
              <figure className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur-xl">
                <div className="flex items-center gap-2 border-b border-white/10 bg-white/5 px-4 py-2.5">
                  <span aria-hidden="true" className="size-2.5 rounded-full bg-peligro-400" />
                  <span aria-hidden="true" className="size-2.5 rounded-full bg-alerta-400" />
                  <span aria-hidden="true" className="size-2.5 rounded-full bg-ok-400" />
                  <span className="ml-3 truncate font-mono text-xs text-white/50">
                    {EJEMPLOS[ejemplo].archivo}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1 px-4 pt-3">
                  {EJEMPLOS.map((ej, indice) => (
                    <button
                      key={ej.archivo}
                      type="button"
                      aria-pressed={indice === ejemplo}
                      onClick={() => setEjemplo(indice)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        indice === ejemplo
                          ? 'bg-white/15 text-white'
                          : 'text-white/50 hover:text-white/90'
                      }`}
                    >
                      {ej.titulo}
                    </button>
                  ))}
                </div>

                <pre className="overflow-x-auto px-4 py-4 font-mono text-[13px] leading-relaxed">
                  {EJEMPLOS[ejemplo].lineas.map((linea, indiceLinea) => (
                    <span key={indiceLinea} className="block whitespace-pre">
                      {linea.map(([tipo, segmento], indiceSegmento) => (
                        <span key={indiceSegmento} className={COLOR_LINEA[tipo]}>
                          {segmento}
                        </span>
                      ))}
                    </span>
                  ))}
                </pre>

                <div className="flex items-center justify-between border-t border-white/10 px-4 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden="true" className="size-1.5 rounded-full bg-ok-400" />
                    <span className="font-mono text-[11px] text-white/50">Corre en tu navegador</span>
                  </span>
                  <span className="hidden font-mono text-[11px] text-white/40 sm:inline">JS</span>
                </div>
              </figure>
            </div>
          </div>
        </section>

        <section className="mt-12 grid gap-6 sm:grid-cols-3">
          {PILARES.map(({ titulo, texto: cuerpo, icono }) => (
            <div
              key={titulo}
              className="group relative overflow-hidden rounded-2xl bg-superficie p-6 ring-1 ring-ink-200 transition-colors hover:ring-brand-300"
            >
              <div
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-0.5 bg-linear-to-r from-brand-500 to-acento-500 opacity-0 transition-opacity group-hover:opacity-100"
              />
              <span
                aria-hidden="true"
                className="inline-grid size-11 place-items-center rounded-xl bg-linear-to-br from-brand-500/15 to-acento-500/15 text-xl ring-1 ring-brand-500/20 ring-inset"
              >
                {icono}
              </span>
              <h2 className="mt-4 font-semibold text-ink-900">{titulo}</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{cuerpo}</p>
            </div>
          ))}
        </section>

        {/*
          El temario va aquí en vez de en la portada con un "síguiente" que
          rompe al scrapear. Es el mismo contenido que ya existe en MesPrograma y
          no tiene sentido duplicarlo para mostrar cuatro meses y esconder tres.
          La portada enlaza a `/programa`, que lo enseña entero.
        */}
        {!programa ? (
          <section className="mt-12">
            <Cargador texto="Cargando el programa..." />
          </section>
        ) : (
          <section className="mt-12 overflow-hidden rounded-2xl bg-superficie ring-1 ring-ink-200">
            <div className="flex items-center gap-3 border-b border-ink-200 px-6 py-5 sm:px-8">
              <span
                aria-hidden="true"
                className="inline-grid size-9 place-items-center rounded-lg bg-brand-500/10 text-brand-700 ring-1 ring-brand-500/20 ring-inset dark:bg-brand-500/15 dark:text-brand-800 dark:ring-brand-500/25"
              >
                🗺️
              </span>
              <div>
                <h2 className="text-lg font-semibold text-ink-900">Qué hay dentro</h2>
                <p className="text-sm text-ink-500">El camino completo, de cero al despliegue.</p>
              </div>
            </div>

            {/*
              En el DOM la etiqueta va antes que la cifra: una lista de
              definiciones se lee "meses, siete", no "siete, meses". La cifra se
              enseña arriba con `flex-col-reverse`, que es la parte que es solo
              para los ojos. Poner el `dd` antes que el `dt` da una lectura de
              pantalla suelta y además es HTML inválido.
            */}
            <dl className="grid divide-y divide-ink-200 sm:grid-cols-4 sm:divide-x sm:divide-y-0">
              {[
                { etiqueta: 'Meses', valor: programa.meta.mesesTotales },
                { etiqueta: 'Semanas', valor: semanas },
                { etiqueta: 'Lecciones escritas', valor: programa.meta.lessonCount },
                { etiqueta: 'Ejercicios', valor: ejercicios },
              ].map(({ etiqueta, valor }) => (
                <div key={etiqueta} className="flex flex-col-reverse gap-1 px-6 py-5 sm:px-8">
                  <dt className="text-xs font-medium tracking-wide text-ink-500 uppercase">
                    {etiqueta}
                  </dt>
                  <dd className="text-3xl font-extrabold text-brand-700 tabular-nums dark:text-brand-800">
                    {valor}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ink-200 px-6 py-5 sm:px-8">
              <p className="max-w-xl text-sm text-ink-600">
                {mesesListos === programa.meta.mesesTotales
                  ? `Los ${programa.meta.mesesTotales} meses ya tienen lecciones escritas.`
                  : mesesListos === 1
                    ? 'El primer mes ya tiene lecciones escritas.'
                    : `Los primeros ${mesesListos} meses tienen lecciones escritas.`}{' '}
                {mesesListos < programa.meta.mesesTotales
                  ? 'Los demás están planificados y se enseñan igual en el programa, sin enlaces a un temario vacío. '
                  : ''}{' '}
                Cada mes termina en un proyecto.
              </p>

              <Link to="/programa">
                <Boton variante="secundario">Leer el temario completo</Boton>
              </Link>
            </div>
          </section>
        )}

        <section className="relative mt-12 overflow-hidden rounded-3xl bg-noche px-6 py-12 text-white shadow-lg shadow-noche/40 ring-1 ring-white/10 sm:px-12 sm:py-14">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 -left-16 size-[26rem] rounded-full bg-brand-600/30 blur-[100px]" />
            <div className="absolute -right-16 -bottom-24 size-[24rem] rounded-full bg-acento-600/25 blur-[100px]" />
            <div className="fondo-rejilla absolute inset-0 opacity-50" />
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-brand-400/70 to-transparent" />
          </div>

          <div className="relative flex flex-col items-center gap-6 text-center sm:flex-row sm:justify-between sm:text-left">
            <div className="max-w-xl">
              <h2 className="text-2xl font-bold sm:text-3xl">Empieza hoy, gratis.</h2>
              <p className="mt-2 text-white/70">
                Lecciones, ejercicios que corren tests de verdad en tu navegador y un proyecto cada
                mes. Tu panel guarda el avance.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {estado === 'anonimo' ? (
                <Link
                  to="/registro"
                  className="inline-flex items-center justify-center rounded-full bg-linear-to-br from-coral-500 to-coral-600 px-5 py-2.5 text-sm font-semibold text-coral-950 transition-all hover:from-coral-400 hover:to-coral-500"
                >
                  Crear cuenta
                </Link>
              ) : null}
              <Link
                to="/programa"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
              >
                Ver el temario
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-ink-200 bg-superficie">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 py-6 text-center text-sm text-ink-500 sm:flex-row">
          <span className="inline-flex items-center gap-2">
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              className="size-5 rounded-md ring-1 ring-ink-200"
            />
            JS Full Stack Academy
          </span>
          <span>Cada proyecto sale del temario, no de una carpeta aparte.</span>
        </div>
      </footer>
    </div>
  );
}